<template>
  <section class="message-detail-panel">
    <template v-if="conversation">
      <div class="message-detail-panel__main">
        <header class="message-detail-panel__header">
          <z-button
            class="message-detail-panel__back"
            :icon="ArrowLeft"
            link
            aria-label="返回列表"
            @click="emit('back')"
          />
          <div class="message-detail-panel__identity">
            <div class="message-detail-panel__title-row">
              <h4 class="message-detail-panel__title">{{ conversation.title }}</h4>
              <z-icon
                v-if="conversation.kind === 'direct' && conversation.online"
                class="message-detail-panel__online"
              >
                <Monitor />
              </z-icon>
            </div>
            <p v-if="conversation.kind !== 'notice' && conversation.kind !== 'bot'" class="message-detail-panel__sub">
              {{ conversation.subtitle }}
            </p>
            <div v-else class="message-detail-panel__tags">
              <z-tag :type="typeTag.type" size="small" effect="plain">
                {{ typeTag.label }}
              </z-tag>
              <z-tag v-if="conversation.sender" size="small" effect="plain">
                {{ conversation.sender }}
              </z-tag>
            </div>
          </div>
          <z-button
            v-if="conversation.kind !== 'notice' && conversation.kind !== 'bot'"
            class="message-detail-panel__star"
            :class="{ 'is-starred': conversation.starred }"
            :icon="Star"
            link
            aria-label="收藏"
            @click="emit('toggle-star', conversation.id)"
          />
          <z-button
            v-if="conversation.kind !== 'notice' && conversation.kind !== 'bot'"
            :icon="CirclePlus"
            link
            aria-label="发起会话"
            @click="emit('invite')"
          />
          <z-dropdown trigger="click" @command="handleCommand">
            <z-button :icon="MoreFilled" link aria-label="更多" />
            <template #dropdown>
              <z-dropdown-menu>
                <z-dropdown-item v-if="conversation.unread > 0" command="read">
                  标为已读
                </z-dropdown-item>
                <z-dropdown-item v-else command="unread">
                  标为未读
                </z-dropdown-item>
                <z-dropdown-item command="hide">不显示</z-dropdown-item>
                <z-dropdown-item command="remove">删除</z-dropdown-item>
              </z-dropdown-menu>
            </template>
          </z-dropdown>
        </header>

        <z-watermark
          class="message-detail-panel__thread"
          :content="watermark"
          :font="{ fontSize: 14, color: 'rgba(0, 0, 0, 0.06)' }"
          :gap="[180, 140]"
          :z-index="1"
        >
          <div ref="threadRef" class="message-detail-panel__scroll">
            <div
              v-for="(item, index) in threadItems"
              :key="item.message.id"
            >
              <div v-if="item.showTime" class="message-detail-panel__time">
                {{ formatDateTime(item.message.createTime) }}
              </div>
              <MessageBubble
                :message="item.message"
                :show-sender="showSender(item.message)"
                :alert="conversation.type === 'alert' && index === 0"
              />
            </div>
          </div>
        </z-watermark>

        <MessageComposer
          v-if="replyable"
          ref="composerRef"
          @send="emit('send', $event)"
          @image="emit('image', $event)"
          @file="emit('file', $event)"
        />
        <footer v-else class="message-detail-panel__footer">
          <span class="message-detail-panel__hint">{{ footerHint }}</span>
          <z-button
            v-if="conversation.bizTab"
            type="primary"
            @click="emit('open', conversation)"
          >
            打开业务
          </z-button>
        </footer>
      </div>

      <aside
        v-if="isGroup"
        class="message-detail-panel__members"
        aria-label="群成员"
      >
        <header class="message-detail-panel__members-head">
          群成员 · {{ groupMembers.length }}
        </header>
        <div class="message-detail-panel__member-list">
          <MessageGroupMember
            v-for="member in groupMembers"
            :key="member.id"
            :member="member"
            @command="handleMemberCommand"
          />
        </div>
      </aside>
    </template>

    <z-empty v-else class="message-detail-panel__empty" description="请选择左侧会话" />

    <MessageMemberProfileDialog
      v-model="profileVisible"
      :member="profileMember"
      @direct-chat="handleProfileDirectChat"
    />
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import {
  ArrowLeft,
  CirclePlus,
  Monitor,
  MoreFilled,
  Star,
} from '@zoryn/icons'
import ZButton from '@zoryn/components/button'
import ZDropdown, {
  ZDropdownItem,
  ZDropdownMenu,
} from '@zoryn/components/dropdown'
import ZEmpty from '@zoryn/components/empty'
import ZIcon from '@zoryn/components/icon'
import { ZMessage } from '@zoryn/components/message'
import ZTag from '@zoryn/components/tag'
import ZWatermark from '@zoryn/components/watermark'
import { canReply } from '../utils/conversation-utils'
import { formatDateTime, shouldShowMessageTime } from '../utils/message-format'
import {
  TYPE_TAG_MAP,
  type ChatMessage,
  type Conversation,
  type ConversationMember,
  type GroupMemberMenuCommand,
} from '../types'
import MessageBubble from './MessageBubble.vue'
import MessageComposer from './MessageComposer.vue'
import MessageGroupMember from './MessageGroupMember.vue'
import MessageMemberProfileDialog from './MessageMemberProfileDialog.vue'
import '@zoryn/components/button/style'
import '@zoryn/components/dropdown/style'
import '@zoryn/components/empty/style'
import '@zoryn/components/icon/style'
import '@zoryn/components/tag/style'
import '@zoryn/components/watermark/style'

const props = defineProps<{
  conversation: Conversation | null
  messages: ChatMessage[]
  watermark?: string
}>()

const emit = defineEmits<{
  back: []
  invite: []
  'toggle-star': [id: string]
  'mark-read': [id: string]
  'mark-unread': [id: string]
  hide: [id: string]
  remove: [id: string]
  open: [conversation: Conversation]
  send: [text: string]
  image: [file: File]
  file: [file: File]
  'direct-chat': [member: ConversationMember]
}>()

const threadRef = ref<HTMLElement>()
const composerRef = ref<{ insertMention: (name: string) => void }>()
const profileVisible = ref(false)
const profileMember = ref<ConversationMember | null>(null)

const typeTag = computed(() =>
  props.conversation?.type
    ? TYPE_TAG_MAP[props.conversation.type]
    : TYPE_TAG_MAP.system
)

const replyable = computed(() => canReply(props.conversation))

const isGroup = computed(() => props.conversation?.kind === 'group')

const groupMembers = computed(() => props.conversation?.members ?? [])

const footerHint = computed(() => {
  if (!props.conversation) return ''
  if (props.conversation.bizTab) return '可跳转到关联业务处理'
  if (props.conversation.type === 'alert') return '告警通知，不可回复'
  return '系统通知，不可回复'
})

const threadItems = computed(() =>
  props.messages.map((message, index) => ({
    message,
    showTime: shouldShowMessageTime(
      props.messages[index - 1]?.createTime,
      message.createTime
    ),
  }))
)

function showSender(message: ChatMessage) {
  return props.conversation?.kind === 'group' && message.from === 'peer'
}

function handleCommand(command: string) {
  if (!props.conversation) return
  if (command === 'read') emit('mark-read', props.conversation.id)
  if (command === 'unread') emit('mark-unread', props.conversation.id)
  if (command === 'hide') emit('hide', props.conversation.id)
  if (command === 'remove') emit('remove', props.conversation.id)
}

async function copyField(value: string | undefined, label: string) {
  const text = value?.trim()
  if (!text) {
    ZMessage.warning(`暂无${label}`)
    return
  }
  try {
    await navigator.clipboard.writeText(text)
    ZMessage.success(`已复制${label}`)
  } catch {
    ZMessage.error(`复制${label}失败`)
  }
}

function handleMemberCommand(
  command: GroupMemberMenuCommand,
  member: ConversationMember
) {
  if (command === 'direct') {
    emit('direct-chat', member)
    return
  }
  if (command === 'profile') {
    profileMember.value = member
    profileVisible.value = true
    return
  }
  if (command === 'mention') {
    composerRef.value?.insertMention(member.name)
    return
  }
  if (command === 'copy-account') {
    void copyField(member.username, '账号')
    return
  }
  if (command === 'copy-email') {
    void copyField(member.email, '邮箱地址')
  }
}

function handleProfileDirectChat(member: ConversationMember) {
  profileVisible.value = false
  emit('direct-chat', member)
}

async function scrollToBottom() {
  await nextTick()
  const el = threadRef.value
  if (!el) return
  el.scrollTop = el.scrollHeight
}

watch(
  () => [props.conversation?.id, props.messages.length] as const,
  () => {
    void scrollToBottom()
  },
  { immediate: true }
)
</script>
